import React, { useState, useEffect, useRef, useMemo } from "react";
import { Listbox, ListboxOption } from "./listbox";
import { Input } from "./input";
import { Badge } from "./badge";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { eventoSinEspacioAsignado } from "../utils/espacios";
import AsignarEspaciosWizard from "./eventos/AsignarEspaciosWizard";
import { useNavigate } from "react-router-dom";
import { getValorEvento, eventoPagado } from "../utils/valorEvento";
import { esSoloLectura } from "../utils/roles";
import {
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const EVENT_TYPE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "scouting", label: "Scouting" },
  { value: "event", label: "Evento" },
  { value: "reunion", label: "Reunión" },
  { value: "bloqueo", label: "Bloqueo" },
];

const EVENT_TYPE_CONFIG = {
  scouting: { label: "Scouting", badge: "orange" },
  event: { label: "Evento", badge: "emerald" },
  reunion: { label: "Reunión", badge: "blue" },
  bloqueo: { label: "Bloqueo", badge: "red" },
};

// `timeZone` es la del venue (calendars/default.timezone, vía useVenue). Sin
// ella el evento se formatea en la zona del navegador de quien mira, así que
// un owner o admin fuera de México vería la hora corrida.
function formatDate(val, timeZone) {
  let dateObj = val?.toDate ? val.toDate() : val ? new Date(val) : null;
  if (!dateObj || isNaN(dateObj.getTime())) return "-";
  const weekday = dateObj.toLocaleDateString("es-MX", { weekday: "short", timeZone });
  const dateStr = dateObj.toLocaleDateString("es-MX", { timeZone });
  const timeStr = dateObj.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
  return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${dateStr}, ${timeStr}`;
}

export default function EventDetails() {
  const navigate = useNavigate();
  const {
    selectedVenueId,
    selectedVenue,
    events,
    eventsLoading,
    espaciosActivos,
    espaciosPorId,
    permiteEventosSimultaneos,
    calendarTimezone,
  } = useVenue();
  const { userInfo, user } = useAuth();
  // Visor e inversionista no asignan espacios (P-FRA-13): el aviso es una
  // tarea del owner y su botón escribe en los eventos.
  const soloLectura = esSoloLectura(userInfo);
  const [showWizard, setShowWizard] = useState(false);

  // Eventos que quedaron sin espacio al encender multiespacio: cada uno
  // bloquea el venue entero hasta que alguien lo revise.
  const sinEspacio = useMemo(
    () =>
      (events || [])
        .filter((ev) =>
          eventoSinEspacioAsignado(ev, {
            espaciosActivos,
            permiteEventosSimultaneos,
          })
        )
        .sort(
          (a, b) =>
            (a.startTime?.toDate?.()?.getTime() || 0) -
            (b.startTime?.toDate?.()?.getTime() || 0)
        ),
    [events, espaciosActivos, permiteEventosSimultaneos]
  );
  const [lastVisited, setLastVisited] = useState(() => {
    const id = sessionStorage.getItem("eventLastVisited");
    if (id) sessionStorage.removeItem("eventLastVisited");
    return id;
  });
  const lastVisitedRef = useRef(null);

  // Scroll to last visited event and clear highlight after delay
  useEffect(() => {
    if (lastVisited && lastVisitedRef.current) {
      lastVisitedRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      const timer = setTimeout(() => setLastVisited(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastVisited, eventsLoading]);
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showCancelados, setShowCancelados] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("eventDetailsView") || "cards");

  const handleViewChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem("eventDetailsView", mode);
  };

  const loading = eventsLoading;

  const filteredEvents = events
    .filter((event) => {
      if (!showCancelados && event.status === "cancelado") return false;
      if (eventType !== "all" && event.eventType !== eventType) return false;
      if (search && event.nombre) {
        if (!event.nombre.toLowerCase().includes(search.toLowerCase()))
          return false;
      } else if (search && !event.nombre) {
        return false;
      }
      let eventStart = event.startTime;
      if (eventStart && eventStart.toDate) {
        eventStart = eventStart.toDate();
      } else if (eventStart) {
        eventStart = new Date(eventStart);
      } else {
        eventStart = null;
      }
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (!eventStart || eventStart < fromDate) return false;
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (!eventStart || eventStart > toDate) return false;
      }
      if (!showPastEvents) {
        const now = new Date();
        const cutoff = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
        cutoff.setDate(cutoff.getDate() - 3);
        if (!eventStart || eventStart < cutoff) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aStart = a.startTime?.toDate
        ? a.startTime.toDate()
        : new Date(a.startTime);
      const bStart = b.startTime?.toDate
        ? b.startTime.toDate()
        : new Date(b.startTime);
      return aStart - bStart;
    });

  // Split into current and past for the divider
  const pastCutoff = new Date();
  pastCutoff.setDate(pastCutoff.getDate() - 3);
  pastCutoff.setHours(0, 0, 0, 0);

  const currentEvents = filteredEvents.filter((e) => {
    let start = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
    return start >= pastCutoff;
  });
  const pastEvents = filteredEvents.filter((e) => {
    let start = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
    return start < pastCutoff;
  });

  const goToEvent = (eventId) => {
    sessionStorage.setItem("eventLastVisited", eventId);
    navigate(`/dashboard/eventos/${eventId}`, { state: { from: "eventos" } });
  };

  const renderEventCard = (event) => {
    const config =
      EVENT_TYPE_CONFIG[event.eventType] || EVENT_TYPE_CONFIG.event;
    const isHighlighted = lastVisited === event.id;
    return (
      <div
        key={event.id}
        ref={isHighlighted ? lastVisitedRef : undefined}
        className={`group rounded-xl border bg-white dark:bg-zinc-800/50 p-5 shadow-sm flex flex-col gap-3 cursor-pointer transition-all hover:shadow-md ${event.status === "cancelado" ? "border-red-200 dark:border-red-800/40 opacity-60" : isHighlighted ? "border-[#1b4d2e] ring-2 ring-[#1b4d2e]/20 shadow-md" : "border-zinc-200 dark:border-zinc-700/60 hover:border-zinc-300 dark:hover:border-zinc-600"}`}
        onClick={() => goToEvent(event.id)}
        title="Ver detalles del evento"
      >
        {/* Badges row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge color={config.badge}>{config.label}</Badge>
          {event.status === "cancelado" ? (
            <Badge color="red">Cancelado</Badge>
          ) : (
            event.eventType === "event" &&
            (!getValorEvento(event) ? (
              <Badge color="zinc">Sin cotizar</Badge>
            ) : eventoPagado(event) ? (
              <Badge color="emerald">Pagado</Badge>
            ) : (
              <Badge color="yellow">Pendiente</Badge>
            ))
          )}
        </div>

        {/* Event name */}
        <div className="text-base font-semibold text-zinc-900 dark:text-white leading-snug break-words">
          {event.nombre || (
            <span className="italic text-zinc-400">Sin nombre</span>
          )}
        </div>

        {/* Notes preview — before dates so mt-auto keeps dates aligned */}
        {event.notes && (
          <div className="flex items-start gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-700/40">
            <DocumentTextIcon
              className="h-3.5 w-3.5 shrink-0 mt-0.5"
              style={{ color: ACCENT }}
            />
            <span className="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2">
              {event.notes}
            </span>
          </div>
        )}

        {/* Dates — always pinned to bottom */}
        <div className="space-y-1.5 mt-auto pt-2 border-t border-zinc-100 dark:border-zinc-700/40">
          <div className="flex items-center gap-2">
            <ClockIcon
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: ACCENT }}
            />
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              {formatDate(event.startTime, calendarTimezone)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ClockIcon
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: ACCENT }}
            />
            <span className="text-xs text-zinc-500 dark:text-zinc-400">
              {formatDate(event.endTime, calendarTimezone)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const getStatusBadge = (event) => {
    if (event.status === "cancelado") return <Badge color="red">Cancelado</Badge>;
    if (event.eventType !== "event") return null;
    if (!getValorEvento(event)) return <Badge color="zinc">Sin cotizar</Badge>;
    if (eventoPagado(event)) return <Badge color="emerald">Pagado</Badge>;
    return <Badge color="yellow">Pendiente</Badge>;
  };

  const renderEventTable = (evts, dimmed = false) => (
    <div className={`rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden ${dimmed ? "opacity-75" : ""}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/80">
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Nombre</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 hidden sm:table-cell">Tipo</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Estado</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 hidden md:table-cell">Inicio</th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 hidden lg:table-cell">Fin</th>
          </tr>
        </thead>
        <tbody>
          {evts.map((event) => {
            const config = EVENT_TYPE_CONFIG[event.eventType] || EVENT_TYPE_CONFIG.event;
            const isHighlighted = lastVisited === event.id;
            return (
              <tr
                key={event.id}
                ref={isHighlighted ? lastVisitedRef : undefined}
                className={`border-b border-zinc-100 dark:border-zinc-700/40 cursor-pointer transition-all hover:bg-zinc-50 dark:hover:bg-zinc-700/30 ${event.status === "cancelado" ? "opacity-60" : ""} ${isHighlighted ? "bg-[#1b4d2e]/5" : ""}`}
                onClick={() => goToEvent(event.id)}
              >
                <td className="px-4 py-3">
                  <span
                    className="font-medium text-zinc-900 dark:text-white break-words"
                    style={{ hyphens: "auto", overflowWrap: "break-word" }}
                    lang="es"
                  >
                    {event.nombre || <span className="italic text-zinc-400">Sin nombre</span>}
                  </span>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <Badge color={config.badge}>{config.label}</Badge>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(event)}
                </td>
                <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400 whitespace-nowrap hidden md:table-cell">
                  {formatDate(event.startTime, calendarTimezone)}
                </td>
                <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400 whitespace-nowrap hidden lg:table-cell">
                  {formatDate(event.endTime, calendarTimezone)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <ClipboardDocumentCheckIcon
          className="h-6 w-6"
          style={{ color: ACCENT }}
        />
        <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">
          Eventos
        </h1>
        {selectedVenue && (
          <span className="text-lg text-zinc-400 font-normal hidden sm:inline">
            – {selectedVenue.name}
          </span>
        )}
        {/* Abre el formulario del calendario (con su revisión de choques de
            fechas y espacios): un solo lugar donde se crean eventos. */}
        {!soloLectura && selectedVenue && (
          <button
            type="button"
            onClick={() => navigate("/dashboard/calendario?nuevo=1")}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            Nuevo evento
          </button>
        )}
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Consulta y administra los eventos de tu venue.
      </p>

      {/* Migración: los eventos creados antes de activar la reserva por salón
          no tienen espacio y bloquean el venue completo. Se avisa aquí porque
          es la lista donde se ven todos, y se arreglan en bloque. */}
      {sinEspacio.length > 0 && !soloLectura && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
          <div className="flex flex-wrap items-center gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                {sinEspacio.length}{" "}
                {sinEspacio.length === 1
                  ? "evento próximo no tiene espacio asignado"
                  : "eventos próximos no tienen espacio asignado"}
              </p>
              <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-400/80">
                Mientras no lo tengan, cada uno bloquea TODOS tus espacios — en
                el calendario y en la disponibilidad de tu página pública.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className="shrink-0 rounded-lg px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Asignar espacios
            </button>
          </div>
        </div>
      )}

      <AsignarEspaciosWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        venueId={selectedVenueId}
        eventos={sinEspacio}
        espaciosActivos={espaciosActivos}
        espaciosPorId={espaciosPorId}
        userInfo={userInfo}
        user={user}
      />

      {/* Filters card */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Buscar
            </label>
            <Input
              type="text"
              placeholder="Nombre del evento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Tipo
            </label>
            <Listbox
              value={eventType}
              onChange={setEventType}
              className="w-full"
            >
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <ListboxOption key={opt.value} value={opt.value}>
                  {opt.label}
                </ListboxOption>
              ))}
            </Listbox>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Desde
            </label>
            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full"
              />
              {dateFrom && (
                <button
                  type="button"
                  className="text-zinc-400 hover:text-red-500 text-lg px-1 transition"
                  onClick={() => setDateFrom("")}
                  title="Limpiar"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Hasta
            </label>
            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full"
              />
              {dateTo && (
                <button
                  type="button"
                  className="text-zinc-400 hover:text-red-500 text-lg px-1 transition"
                  onClick={() => setDateTo("")}
                  title="Limpiar"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Past events toggle + count + view toggle */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700/40">
          <span className="text-xs text-zinc-400">
            {filteredEvents.length}{" "}
            {filteredEvents.length === 1 ? "evento" : "eventos"}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowCancelados((v) => !v)}
              className="text-xs font-medium transition"
              style={{ color: ACCENT }}
            >
              {showCancelados
                ? "Ocultar cancelados"
                : "Mostrar cancelados"}
            </button>
            <button
              type="button"
              onClick={() => setShowPastEvents((v) => !v)}
              className="text-xs font-medium transition"
              style={{ color: ACCENT }}
            >
              {showPastEvents
                ? "Ocultar eventos pasados"
                : "Ver eventos pasados"}
            </button>
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-zinc-200 dark:border-zinc-700/40">
              <button
                type="button"
                onClick={() => handleViewChange("cards")}
                className={`p-1.5 rounded-lg transition ${viewMode === "cards" ? "bg-zinc-100 dark:bg-zinc-700" : "hover:bg-zinc-50 dark:hover:bg-zinc-700/40"}`}
                title="Tarjetas"
              >
                <Squares2X2Icon className={`h-4 w-4 ${viewMode === "cards" ? "text-zinc-900 dark:text-white" : "text-zinc-400"}`} />
              </button>
              <button
                type="button"
                onClick={() => handleViewChange("table")}
                className={`p-1.5 rounded-lg transition ${viewMode === "table" ? "bg-zinc-100 dark:bg-zinc-700" : "hover:bg-zinc-50 dark:hover:bg-zinc-700/40"}`}
                title="Tabla"
              >
                <TableCellsIcon className={`h-4 w-4 ${viewMode === "table" ? "text-zinc-900 dark:text-white" : "text-zinc-400"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Event grids */}
      {loading ? (
        <div className="text-center py-12 text-zinc-400">
          Cargando eventos...
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          No hay eventos para mostrar.
        </div>
      ) : (
        <>
          {/* Past events (shown first since sorted ascending) */}
          {showPastEvents && pastEvents.length > 0 && (
            <>
              {viewMode === "cards" ? (
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-6 opacity-75">
                  {pastEvents.map((event) => renderEventCard(event))}
                </div>
              ) : (
                <div className="mb-6">
                  {renderEventTable(pastEvents, true)}
                </div>
              )}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 h-px bg-zinc-300 dark:bg-zinc-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500 whitespace-nowrap">
                  Eventos próximos
                </span>
                <div className="flex-1 h-px bg-zinc-300 dark:bg-zinc-600" />
              </div>
            </>
          )}

          {/* Current / upcoming events */}
          {currentEvents.length > 0 ? (
            viewMode === "cards" ? (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {currentEvents.map((event) => renderEventCard(event))}
              </div>
            ) : (
              renderEventTable(currentEvents)
            )
          ) : showPastEvents && pastEvents.length > 0 ? (
            <div className="text-center py-8 text-zinc-400 text-sm">
              No hay eventos próximos.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
